import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { EnergyApiService } from './energy-api.service';
import {
  Customer,
  EnergyBalanceResponse,
  EnergyBalanceTimeBucketResponse,
  Invoice,
  PaginatedInvoiceList,
} from '../interfaces/metergrid';

/**
 * Energy State Service
 *
 * Central state management for energy-related data with support for mock data.
 * Provides observables for components to subscribe to energy data changes.
 */
@Injectable({
  providedIn: 'root',
})
export class EnergyStateService {
  // Mock data flag - set to false when backend is ready
  private readonly USE_MOCK_DATA = false;

  // State subjects
  private customerSubject = new BehaviorSubject<Customer | null>(null);
  private energyBalanceSubject = new BehaviorSubject<EnergyBalanceResponse | null>(null);
  private energyTimeSeriesSubject = new BehaviorSubject<EnergyBalanceTimeBucketResponse | null>(null);
  private invoicesSubject = new BehaviorSubject<Invoice[]>([]);
  private latestMeterReadingSubject = new BehaviorSubject<{
    id: number;
    timestamp: string;
    value: string;
    obis_code: string;
    measuring_point: string;
  } | null>(null);
  private graphDataSubject = new BehaviorSubject<Array<{
    value: number;
    measuring_point_id: number;
    id: number;
    obis_code: string;
    timestamp: string;
    reason: string;
    origin: string;
  }> | null>(null);
  private connectionStatusSubject = new BehaviorSubject<{
    connected: boolean;
    last_sync?: string;
  }>({ connected: false });
  private loadingSubject = new BehaviorSubject<boolean>(false);

  // Public observables
  customer$ = this.customerSubject.asObservable();
  energyBalance$ = this.energyBalanceSubject.asObservable();
  energyTimeSeries$ = this.energyTimeSeriesSubject.asObservable();
  invoices$ = this.invoicesSubject.asObservable();
  latestMeterReading$ = this.latestMeterReadingSubject.asObservable();
  graphData$ = this.graphDataSubject.asObservable();
  connectionStatus$ = this.connectionStatusSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();

  constructor(private energyApi: EnergyApiService) {}

  // ========================================
  // Public Methods
  // ========================================

  /**
   * Load all energy data for current user
   */
  async loadEnergyData(): Promise<void> {
    this.loadingSubject.next(true);

    try {
      if (this.USE_MOCK_DATA) {
        await this.loadMockData();
      } else {
        await this.loadRealData();
      }
    } catch (error) {
      console.error('Error loading energy data:', error);
    } finally {
      this.loadingSubject.next(false);
    }
  }

  /**
   * Refresh energy balance data
   */
  async refreshEnergyBalance(start?: string, end?: string): Promise<void> {
    if (this.USE_MOCK_DATA) {
      this.energyBalanceSubject.next(this.getMockEnergyBalance());
      return;
    }

    const balance = await this.energyApi.getEnergyBalance({ start, end });
    if (balance) {
      this.energyBalanceSubject.next(balance);
    }
  }

  /**
   * Refresh energy time series data
   */
  async refreshEnergyTimeSeries(
    start?: string,
    end?: string,
    bucket_size: 'hour' | 'day' | 'week' | 'month' = 'day'
  ): Promise<void> {
    if (this.USE_MOCK_DATA) {
      this.energyTimeSeriesSubject.next(this.getMockEnergyTimeSeries());
      return;
    }

    const timeSeries = await this.energyApi.getEnergyBalanceTimeBucket({
      start,
      end,
      bucket_size,
    });
    if (timeSeries) {
      this.energyTimeSeriesSubject.next(timeSeries);
    }
  }

  /**
   * Refresh invoices list
   */
  async refreshInvoices(): Promise<void> {
    if (this.USE_MOCK_DATA) {
      this.invoicesSubject.next(this.getMockInvoices());
      return;
    }

    const result = await this.energyApi.listMyInvoices({ page_size: 50 });
    if (result) {
      this.invoicesSubject.next(result.results);
    }
  }

  /**
   * Refresh graph data
   */
  async refreshGraphData(page_size: number = 100): Promise<void> {
    const graphData = await this.energyApi.getGraphData({
      page_size,
    });
    if (graphData) {
      this.graphDataSubject.next(graphData);
    }
  }

  /**
   * Connect to energy provider
   */
  async connect(): Promise<boolean> {
    if (this.USE_MOCK_DATA) {
      this.connectionStatusSubject.next({
        connected: true,
        last_sync: new Date().toISOString(),
      });
      await this.loadMockData();
      return true;
    }

    const success = await this.energyApi.connectEnergyProvider();
    if (success) {
      await this.loadRealData();
    }
    return success;
  }

  /**
   * Disconnect from energy provider
   */
  async disconnect(): Promise<boolean> {
    if (this.USE_MOCK_DATA) {
      this.connectionStatusSubject.next({ connected: false });
      this.clearData();
      return true;
    }

    const success = await this.energyApi.disconnectEnergyProvider();
    if (success) {
      this.clearData();
    }
    return success;
  }

  /**
   * Sync energy data manually
   */
  async syncData(): Promise<boolean> {
    if (this.USE_MOCK_DATA) {
      await this.loadMockData();
      return true;
    }

    const success = await this.energyApi.syncEnergyData();
    if (success) {
      await this.loadRealData();
    }
    return success;
  }

  /**
   * Download invoice PDF
   */
  async downloadInvoice(invoiceId: number): Promise<void> {
    const url = await this.energyApi.downloadInvoicePdf(invoiceId);
    if (url) {
      // Trigger browser download
      const link = document.createElement('a');
      link.href = url;
      link.download = `rechnung-${invoiceId}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    }
  }

  // ========================================
  // Private Methods - Real Data
  // ========================================

  private async loadRealData(): Promise<void> {
    const [customer, invoices, meterReading, status] = await Promise.all(
      [
        this.energyApi.getMyCustomer(),
        this.energyApi.listMyInvoices({ page_size: 50 }),
        this.energyApi.getLatestMeterReading(),
        this.energyApi.getConnectionStatus(),
      ]
    );

    this.customerSubject.next(customer);
    this.energyBalanceSubject.next(null);
    this.energyTimeSeriesSubject.next(null);
    this.invoicesSubject.next(invoices?.results || []);
    this.latestMeterReadingSubject.next(meterReading);
    this.connectionStatusSubject.next({
      connected: status?.connected || false,
      last_sync: status?.last_sync,
    });
  }

  private clearData(): void {
    this.customerSubject.next(null);
    this.energyBalanceSubject.next(null);
    this.energyTimeSeriesSubject.next(null);
    this.invoicesSubject.next([]);
    this.latestMeterReadingSubject.next(null);
    this.connectionStatusSubject.next({ connected: false });
  }

  // ========================================
  // Mock Data Methods
  // ========================================

  private async loadMockData(): Promise<void> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 800));

    this.customerSubject.next(this.getMockCustomer());
    this.energyBalanceSubject.next(this.getMockEnergyBalance());
    this.energyTimeSeriesSubject.next(this.getMockEnergyTimeSeries());
    this.invoicesSubject.next(this.getMockInvoices());
    this.latestMeterReadingSubject.next({
      id: 482348306,
      timestamp: new Date().toISOString(),
      value: '12543.8',
      obis_code: '1-0:1.8.0',
      measuring_point: 'http://backend.metergrid.de/api/v1/measuringpoints/28653/',
    });
    this.connectionStatusSubject.next({
      connected: true,
      last_sync: new Date().toISOString(),
    });
  }

  private getMockCustomer(): Customer {
    return {
      url: '/api/v1/customers/123/',
      id: 123,
      salutation: 'Herr',
      first_name: 'Max',
      last_name: 'Mustermann',
      email: 'max.mustermann@example.com',
      phone_number: '+49 123 456789',
      address: {
        street: 'Musterstraße',
        street_number: '42',
        city: 'München',
        zip_code: '80331',
        country: 'DE',
      },
      project: '/api/v1/projects/1/',
      panel_name: 'Wohnung 42',
      subscription_start_date: '2024-01-01',
      subscription_state: 'active',
      active_payment_plan_amount: [
        ['gross', 85.50],
        ['tax_rate', 0.19],
        ['net', 71.85]
      ],
      active_measuring_point_serial: 'MP-2024-001',
    };
  }

  private getMockEnergyBalance(): EnergyBalanceResponse {
    return {
      projects: [
        {
          project: 1,
          residual_power_consumption: [
            {
              start: '2024-01-01',
              end: '2024-01-31',
              panel: 'Wohnung 42',
              consumption: '125.50',
              measuring_points: [
                {
                  measuring_point: '/api/v1/measuring-points/1/',
                  throughput: '125.50',
                },
              ],
            },
          ],
          production: [
            {
              start: '2024-01-01',
              end: '2024-01-31',
              panel: 'Dach PV-Anlage',
              consumption: '450.00',
              measuring_points: [
                {
                  measuring_point: '/api/v1/measuring-points/2/',
                  throughput: '450.00',
                },
              ],
            },
          ],
          feedin: [
            {
              start: '2024-01-01',
              end: '2024-01-31',
              panel: 'Netzeinspeisung',
              consumption: '180.00',
              measuring_points: [
                {
                  measuring_point: '/api/v1/measuring-points/3/',
                  throughput: '180.00',
                },
              ],
            },
          ],
          in_house_consumption_total: '270.00',
          residual_power_consumption_total: '125.50',
          production_total: '450.00',
          feedin_total: '180.00',
        },
      ],
      in_house_consumption: '270.00',
      residual_power_consumption: '125.50',
      feedin: '180.00',
    };
  }

  private getMockEnergyTimeSeries(): EnergyBalanceTimeBucketResponse {
    const generateTimeSeries = (
      baseValue: number,
      variance: number,
      days: number = 30
    ) => {
      const series = [];
      const now = new Date();
      for (let i = days; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const randomVariance = (Math.random() - 0.5) * variance;
        series.push({
          time: date.toISOString(),
          value: (baseValue + randomVariance).toFixed(2),
        });
      }
      return series;
    };

    return {
      project: [
        {
          project: 1,
          residual_power_consumption: generateTimeSeries(4.2, 1.5),
          production: generateTimeSeries(15.0, 5.0),
          feedin: generateTimeSeries(6.0, 2.0),
          in_house_consumption: generateTimeSeries(9.0, 3.0),
          in_house_consumption_total: '270.00',
          residual_power_consumption_total: '125.50',
          production_total: '450.00',
          feedin_total: '180.00',
        },
      ],
      in_house_consumption: '270.00',
      residual_power_consumption: '125.50',
      feedin: '180.00',
    };
  }

  private getMockInvoices(): Invoice[] {
    return [
      {
        url: '/api/v1/invoices/1001/',
        id: 1001,
        customer: '/api/v1/customers/123/',
        project: '/api/v1/projects/1/',
        total: '85.50',
        state: 'issued',
        category: 'regular invoice',
        period_start: '2024-01-01',
        period_end: '2024-01-31',
        issue_date: '2024-02-01',
        due_date: '2024-02-15',
        entries: [
          {
            description: 'Stromverbrauch Januar 2024 (125.5 kWh)',
            type: 'DEBIT',
            quantity: '125.50',
            quantity_unit: 'kWh',
            unit_price: '0.35',
            factor: 1,
          },
          {
            description: 'Grundgebühr',
            type: 'DEBIT',
            quantity: '1',
            unit_price: '12.00',
            factor: 1,
          },
          {
            description: 'Eigenverbrauch Gutschrift (270 kWh)',
            type: 'CREDIT',
            quantity: '270.00',
            quantity_unit: 'kWh',
            unit_price: '0.15',
            factor: 1,
          },
        ],
      },
      {
        url: '/api/v1/invoices/1002/',
        id: 1002,
        customer: '/api/v1/customers/123/',
        project: '/api/v1/projects/1/',
        total: '78.20',
        state: 'issued',
        category: 'regular invoice',
        period_start: '2023-12-01',
        period_end: '2023-12-31',
        issue_date: '2024-01-01',
        due_date: '2024-01-15',
        paid_date: '2024-01-10',
        entries: [
          {
            description: 'Stromverbrauch Dezember 2023 (118 kWh)',
            type: 'DEBIT',
            quantity: '118.00',
            quantity_unit: 'kWh',
            unit_price: '0.35',
            factor: 1,
          },
          {
            description: 'Grundgebühr',
            type: 'DEBIT',
            quantity: '1',
            unit_price: '12.00',
            factor: 1,
          },
          {
            description: 'Eigenverbrauch Gutschrift (255 kWh)',
            type: 'CREDIT',
            quantity: '255.00',
            quantity_unit: 'kWh',
            unit_price: '0.15',
            factor: 1,
          },
        ],
      },
      {
        url: '/api/v1/invoices/1003/',
        id: 1003,
        customer: '/api/v1/customers/123/',
        project: '/api/v1/projects/1/',
        total: '92.80',
        state: 'issued',
        category: 'regular invoice',
        period_start: '2023-11-01',
        period_end: '2023-11-30',
        issue_date: '2023-12-01',
        due_date: '2023-12-15',
        paid_date: '2023-12-12',
        entries: [
          {
            description: 'Stromverbrauch November 2023 (142 kWh)',
            type: 'DEBIT',
            quantity: '142.00',
            quantity_unit: 'kWh',
            unit_price: '0.35',
            factor: 1,
          },
          {
            description: 'Grundgebühr',
            type: 'DEBIT',
            quantity: '1',
            unit_price: '12.00',
            factor: 1,
          },
          {
            description: 'Eigenverbrauch Gutschrift (245 kWh)',
            type: 'CREDIT',
            quantity: '245.00',
            quantity_unit: 'kWh',
            unit_price: '0.15',
            factor: 1,
          },
        ],
      },
    ];
  }
}
